import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Link,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { formErrorMsg } from "core/config/constants";
import { useAuthStore } from "core/store/authStore";
import { getDashboardRoute, getProviderLandingRoute } from "core/router/routes";
import "./login.css";

interface LoginFormData {
  email: string;
  password: string;
}

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sessionUpdated = searchParams.get("reason") === "session_updated";
  const { login, isLoading, error, clearError } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  const onSubmit = async (formData: LoginFormData) => {
    clearError();
    try {
      const user = await login(formData.email, formData.password);
      const from = (location.state as any)?.from?.pathname;
      if (from) {
        navigate(from, { replace: true });
        return;
      }
      const destination =
        user.role === 'provider'
          ? await getProviderLandingRoute()
          : getDashboardRoute(user.role);
      navigate(destination, { replace: true });
    } catch {
      // error is already set in the store
    }
  };
  return (
    <div className="login-page">
      <header className="login-brand">
        {" "}
        <Link to="/" className="flex items-center gap-2.5">
          <div className="bg-primary p-1.5 rounded-xl shadow-lg shadow-primary/25">
            <span
              className="material-symbols-outlined text-white text-[22px]"
              style={{ fontVariationSettings: '"FILL" 1' }}
            >
              directions_run
            </span>
          </div>
          <h2 className="text-[1.4rem] font-black tracking-tighter">Errandi</h2>
        </Link>
      </header>
      <div className="login-card">
        {sessionUpdated && (
          <div className="mb-4 flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3">
            <span
              className="material-symbols-outlined text-blue-500 text-[18px] mt-0.5"
              style={{ fontVariationSettings: '"FILL" 1' }}
            >
              info
            </span>
            <p className="text-sm font-medium text-blue-800">
              Your account has been updated. Please log in again to continue
              with your new access level.
            </p>
          </div>
        )}

        {error && <div className="login-error">{error}</div>}

        <form onSubmit={handleSubmit(onSubmit)} className="login-form">
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className={`form-input ${errors.email ? "form-input-error" : ""}`}
              placeholder="you@example.com"
              {...register("email", {
                required: formErrorMsg.required,
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: formErrorMsg.invalidEmail,
                },
              })}
            />
            {errors.email && (
              <span className="form-error-text">{errors.email.message}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <div className="password-wrapper">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                className={`form-input ${errors.password ? "form-input-error" : ""}`}
                placeholder="Enter your password"
                {...register("password", {
                  required: formErrorMsg.required,
                  minLength: {
                    value: 8,
                    message: formErrorMsg.minPass,
                  },
                })}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label="Toggle password visibility"
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            {errors.password && (
              <span className="form-error-text">{errors.password.message}</span>
            )}
          </div>

          <button type="submit" disabled={isLoading} className="login-button">
            {isLoading ? "Logging in…" : "Log in"}
          </button>
        </form>

        <p className="login-footer">
          Don&apos;t have an account?{" "}
          <Link to="/signup" className="login-link">
            Sign up
          </Link>
        </p>
      </div>

      <footer className="w-full px-6 lg:px-20 pb-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between pt-10 border-t border-primary/6 gap-6">
          <div className="flex gap-5">
            <button
              type="button"
              className="text-slate-400 hover:text-primary transition-colors bg-transparent border-0 p-0 cursor-pointer"
            >
              <span className="material-symbols-outlined">
                social_leaderboard
              </span>
            </button>
            <button
              type="button"
              className="text-slate-400 hover:text-primary transition-colors bg-transparent border-0 p-0 cursor-pointer"
            >
              <span className="material-symbols-outlined">share</span>
            </button>
            <button
              type="button"
              className="text-slate-400 hover:text-primary transition-colors bg-transparent border-0 p-0 cursor-pointer"
            >
              <span className="material-symbols-outlined">alternate_email</span>
            </button>
          </div>
          <p className="text-slate-400 text-sm font-medium">
            &copy; 2026 Errandi Technologies Ltd. All rights reserved.
          </p>
          <div className="flex gap-3 text-xs font-semibold text-slate-400">
            <a href="/privacy-policy" className="hover:text-primary transition-colors">
              Privacy
            </a>
            <span>&middot;</span>
            <a href="/terms-of-service" className="hover:text-primary transition-colors">
              Terms
            </a>
            <span>&middot;</span>
            <a href="/cookies-policy" className="hover:text-primary transition-colors">
              Cookies
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Login;
